// Notification kind → visual + routing metadata.
// The backend emits `kind` on every notification (see server._create_notification).
// This map is the single source of truth for colours, icons, labels and
// whether a chime should play when a notification arrives.
import {
  UserPlus,
  ShoppingBag,
  CheckCircle2,
  Clock,
  Target,
  ShieldAlert,
  Bell,
} from "lucide-react";

// Categories used by the filter tabs.
export const CATEGORIES = ["all", "unread", "orders", "leads", "alerts", "system"];

// kind → config
const KINDS = {
  // Blue — New Lead Assigned
  lead_assigned: {
    label: "New Lead Assigned",
    category: "leads",
    accent: "#3B82F6",
    icon: UserPlus,
    important: true,
  },
  // Orange — New Order / Order Submitted
  order_created: {
    label: "New Order",
    category: "orders",
    accent: "#F97316",
    icon: ShoppingBag,
    important: true,
  },
  // Green — Order Status Change (Approved / Completed / Ready / Payment Received)
  order_ready: {
    label: "Order Ready",
    category: "orders",
    accent: "#22C55E",
    icon: CheckCircle2,
    important: true,
  },
  order_status: {
    label: "Order Status",
    category: "orders",
    accent: "#22C55E",
    icon: CheckCircle2,
    important: true,
  },
  order_payment: {
    label: "Payment Received",
    category: "orders",
    accent: "#22C55E",
    icon: CheckCircle2,
    important: true,
  },
  // Yellow — Follow-up Reminder
  followup_created: {
    label: "Follow-up Scheduled",
    category: "leads",
    accent: "#EAB308",
    icon: Clock,
    important: false,
  },
  followup_due: {
    label: "Follow-up Reminder",
    category: "leads",
    accent: "#EAB308",
    icon: Clock,
    important: false,
  },
  // Purple — Target Alerts
  target_50: {
    label: "Target Milestone",
    category: "alerts",
    accent: "#A855F7",
    icon: Target,
    important: true,
  },
  target_last3: {
    label: "Target Deadline",
    category: "alerts",
    accent: "#A855F7",
    icon: Target,
    important: true,
  },
  // Red — Security / Concurrent Login
  security: {
    label: "Security Alert",
    category: "alerts",
    accent: "#EF4444",
    icon: ShieldAlert,
    important: true,
  },
  // Purple — AI Manager proactive nudges
  ai_nudge: {
    label: "AI Manager",
    category: "alerts",
    accent: "#A855F7",
    icon: Target,
    important: true,
  },
  // Grey — Fallback / System / General
  info: {
    label: "System",
    category: "system",
    accent: "#6B7280",
    icon: Bell,
    important: false,
  },
};

/** Resolve a notification's visual + behavioural metadata. */
export function getNotificationMeta(n) {
  const k = (n && n.kind) || "info";
  return KINDS[k] || KINDS.info;
}

/** Should this notification play a chime + browser push? */
export function isImportant(n) {
  return getNotificationMeta(n).important;
}

/** Where should clicking the card navigate the user? */
export function notificationHref(n, role) {
  if (!n) return null;
  const meta = getNotificationMeta(n);
  if (meta.category === "orders") {
    return role === "admin" ? "/admin/orders" : "/app/orders";
  }
  if (n.lead_id) {
    return role === "admin" ? `/admin/leads/${n.lead_id}` : `/app/leads/${n.lead_id}`;
  }
  return null;
}

/** Filter tab predicate. */
export function matchesCategory(n, cat) {
  if (cat === "all") return true;
  if (cat === "unread") return !n.read;
  return getNotificationMeta(n).category === cat;
}

/** Group notifications into Today / Yesterday / Earlier buckets (chronological). */
export function groupByDate(items) {
  const groups = { Today: [], Yesterday: [], Earlier: [] };
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(now);
  const yesterday = today - 86400000;
  for (const n of items) {
    const t = startOfDay(new Date(n.created_at));
    if (t === today) groups.Today.push(n);
    else if (t === yesterday) groups.Yesterday.push(n);
    else groups.Earlier.push(n);
  }
  return groups;
}
